import React, { useState, useRef, useCallback } from "react";
import { ContentHeader } from "@components";
import DataTable from "../components/datatable/DataTable";
import API from "../api";
import toastr from "toastr";

interface Term {
  id: number;
  lab_id: number;
  description: string;
  created: string;
  modified: string;
}

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  zIndex: 1040,
};

const modalWrapper: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 1050,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px",
  overflowY: "auto",
};

const modalBox: React.CSSProperties = {
  width: "100%",
  maxWidth: "520px",
  background: "#1a1f2e",
  borderRadius: "10px",
  border: "1px solid #2d3748",
  boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
};

const modalHeader: React.CSSProperties = {
  padding: "14px 20px",
  borderBottom: "1px solid #2d3748",
  background: "#111827",
  borderRadius: "10px 10px 0 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

const modalFooter: React.CSSProperties = {
  padding: "12px 20px",
  borderTop: "1px solid #2d3748",
  background: "#111827",
  borderRadius: "0 0 10px 10px",
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
};

const inputCls =
  "form-control form-control-sm bg-dark text-white border-secondary";

// ─── Add Modal ────────────────────────────────────────────────────────────────
const AddTermModal: React.FC<{
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ show, onClose, onSuccess }) => {
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setDescription("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      toastr.error("Description is required");
      return;
    }
    setLoading(true);
    try {
      await API.post("termsConditions/createTerm", {
        description: description.trim(),
      });
      toastr.success("Terms & Conditions added successfully");
      setDescription("");
      onSuccess();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to add term");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div style={modalOverlay} onClick={handleClose} />
      <div style={modalWrapper}>
        <div style={modalBox} onClick={(e) => e.stopPropagation()}>
          <div style={modalHeader}>
            <h5 className="mb-0 text-white fw-semibold" style={{ fontSize: "15px" }}>
              <i className="fa fa-plus-circle me-2 text-danger" />
              Create New Terms & Conditions
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              style={{ opacity: 0.6 }}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ padding: "20px" }}>
              <label
                className="form-label"
                style={{ color: "#ebeff5", fontSize: "13px" }}
              >
                Description <span className="text-danger">*</span>
              </label>
              <textarea
                className={inputCls}
                rows={5}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter terms & conditions description..."
                style={{ resize: "vertical" }}
              />
              <div
                className="text-end mt-1"
                style={{ fontSize: "11px", color: "#4b5563" }}
              >
                {description.length} / 500
              </div>
            </div>

            <div style={modalFooter}>
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-light px-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-1" /> Save
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// ─── Edit Modal ───────────────────────────────────────────────────────────────
const EditTermModal: React.FC<{
  show: boolean;
  term: Term | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ show, term, onClose, onSuccess }) => {
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (term) setDescription(term.description || "");
  }, [term]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) {
      toastr.error("Description is required");
      return;
    }
    setLoading(true);
    try {
      await API.put(`termsConditions/updateTerm/${term?.id}`, {
        description: description.trim(),
      });
      toastr.success("Terms & Conditions updated successfully");
      onSuccess();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to update term");
    } finally {
      setLoading(false);
    }
  };

  if (!show || !term) return null;

  return (
    <>
      <div style={modalOverlay} onClick={handleClose} />
      <div style={modalWrapper}>
        <div style={modalBox} onClick={(e) => e.stopPropagation()}>
          <div style={modalHeader}>
            <h5 className="mb-0 text-white fw-semibold" style={{ fontSize: "15px" }}>
              <i className="fa fa-edit me-2 text-warning" />
              Update Terms & Conditions
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              style={{ opacity: 0.6 }}
            />
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ padding: "20px" }}>
              <label
                className="form-label"
                style={{ color: "#9ca3af", fontSize: "13px" }}
              >
                Description <span className="text-danger">*</span>
              </label>
              <textarea
                className={inputCls}
                rows={5}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter terms & conditions description..."
                style={{ resize: "vertical" }}
              />
              <div
                className="text-end mt-1"
                style={{ fontSize: "11px", color: "#4b5563" }}
              >
                {description.length} / 500
              </div>
            </div>

            <div style={modalFooter}>
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-light px-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1" role="status" />
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fa fa-save me-1" /> Update
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const TermsConditions: React.FC = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const tableRef = useRef<any>(null);

  const fetchTerms = useCallback(async (_page: number, search: string) => {
    try {
      const res = await API.get("termsConditions/getTerms");
      const raw = res.data?.data ?? res.data ?? [];
      const allData: Term[] = Array.isArray(raw) ? raw : [];
      const filtered = search
        ? allData.filter((t) =>
            (t.description ?? "").toLowerCase().includes(search.toLowerCase())
          )
        : allData;
      return { data: filtered, total: filtered.length };
    } catch {
      toastr.error("Failed to fetch terms");
      return { data: [], total: 0 };
    }
  }, []);

  const handleEdit = useCallback((term: Term) => {
    setSelectedTerm(term);
    setShowEditModal(true);
  }, []);

  const handleDelete = useCallback(async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this entry?")) return;
    try {
      await API.delete(`termsConditions/deleteTerm/${id}`);
      toastr.success("Terms & Conditions deleted successfully");
      tableRef.current?.refetch();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to delete term");
    }
  }, []);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const columns = [
    {
      header: "ID",
      id: "serial",
      cell: ({ row }: any) => (
        <span style={{ color: "#ffffff", fontSize: "13px" }}>
          {typeof row.index === "number" ? row.index + 1 : row.original?.id ?? "-"}
        </span>
      ),
    },
    {
      header: "Description",
      accessorKey: "description",
      cell: ({ row }: any) => (
        <span
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontSize: "13px",
            lineHeight: "1.5",
            maxWidth: "500px",
            color: "#ffffff",
          }}
        >
          {typeof row.original?.description === "string" &&
          row.original.description.trim() !== ""
            ? row.original.description
            : "-"}
        </span>
      ),
    },
    {
      header: "Created",
      accessorKey: "created",
      cell: ({ row }: any) => (
        <span style={{ fontSize: "12px", color: "#d1d5db", whiteSpace: "nowrap" }}>
          {formatDate(row.original?.created)}
        </span>
      ),
    },
    {
      header: "Last Modified",
      accessorKey: "modified",
      cell: ({ row }: any) => (
        <span style={{ fontSize: "12px", color: "#d1d5db", whiteSpace: "nowrap" }}>
          {formatDate(row.original?.modified)}
        </span>
      ),
    },
    {
      header: "Action",
      id: "actions",
      cell: ({ row }: any) => {
        const term: Term = row.original;
        return (
          <div className="d-flex justify-content-center gap-1">
            <button
              className="btn btn-light"
              onClick={() => handleEdit(term)}
              title="Edit"
            >
              <i className="fa fa-edit" />
            </button>
            <button
              className="btn btn-light"
              onClick={() => handleDelete(term.id)}
              title="Delete"
            >
              <i className="fa fa-trash" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ContentHeader title="Terms & Conditions" />
        <button className="btn btn-light" onClick={() => setShowAddModal(true)}>
          <i className="fa fa-plus me-1" /> Create New
        </button>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="card card-outline card-primary">
            <div className="card-body">
              <DataTable
                ref={tableRef}
                columns={columns}
                queryFn={fetchTerms}
                queryKey={["terms"]}
              />
            </div>
          </div>
        </div>
      </section>

      <AddTermModal
        show={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          tableRef.current?.refetch();
        }}
      />

      <EditTermModal
        show={showEditModal}
        term={selectedTerm}
        onClose={() => {
          setShowEditModal(false);
          setSelectedTerm(null);
        }}
        onSuccess={() => {
          setShowEditModal(false);
          setSelectedTerm(null);
          tableRef.current?.refetch();
        }}
      />
    </div>
  );
};

export default TermsConditions;